import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Alert, useTheme, useMediaQuery, alpha, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import DigitalSignature from '@ui/components/DigitalSignature';
import collaboratorAPI from '@infrastructure/collaborator-api';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { updateNDASignedStatus } from '@application/features/auth/authSlice';
import { fetchCollaboratorNDAStatusAsync } from '@application/features/auth/authThunk';
import { toast } from 'react-toastify';
const NDAModal = ({ collaboratorId, ndaDocuments, open }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const signatureRef = useRef(null);
    const [hasSignature, setHasSignature] = useState(false);
    const [pdfBlobUrl, setPdfBlobUrl] = useState('');
    const [isSigning, setIsSigning] = useState(false);
    const [signatureError, setSignatureError] = useState('');
    const [selectedLanguageId, setSelectedLanguageId] = useState('');
    // Initialize selected language - default to English
    useEffect(() => {
        if (ndaDocuments.length > 0 && !selectedLanguageId) {
            // Try to find English NDA first
            const englishDoc = ndaDocuments.find(doc => doc.language?.code?.toLowerCase() === 'en');
            // Use English or fall back to first available
            const initialDoc = englishDoc || ndaDocuments[0];
            if (initialDoc.languageId) {
                setSelectedLanguageId(initialDoc.languageId);
            }
        }
    }, [ndaDocuments, selectedLanguageId]);
    // Get currently selected document
    const selectedDocument = ndaDocuments.find(doc => doc.languageId === selectedLanguageId);
    // Load PDF when language selection changes
    useEffect(() => {
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl('');
        }
        if (open && selectedDocument?.url) {
            fetch(selectedDocument.url, {
                method: 'GET',
                mode: 'cors',
                headers: {
                    Accept: 'application/pdf,*/*'
                }
            })
                .then((response) => response.arrayBuffer())
                .then((arrayBuffer) => {
                const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfBlobUrl(url);
            })
                .catch((err) => {
                console.error('Error fetching PDF:', err);
                toast.error('Failed to load NDA document');
            });
        }
    }, [open, selectedDocument?.url]);
    // Cleanup blob URL when modal closes
    useEffect(() => {
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
    }, [pdfBlobUrl]);
    const handleSignatureChange = (hasSig) => {
        setHasSignature(hasSig);
        if (hasSig) {
            setSignatureError('');
        }
    };
    const handleClearSignature = () => {
        signatureRef.current?.clearSignature();
    };
    const handleSign = async () => {
        const signature = signatureRef.current?.getSignature();
        if (!signature) {
            setSignatureError(t('eventRegistration.form.fieldRequired'));
            return;
        }
        if (!selectedLanguageId) {
            toast.error('Please select a language');
            return;
        }
        setIsSigning(true);
        setSignatureError('');
        try {
            await collaboratorAPI.signCollaboratorNDA(collaboratorId, signature, selectedLanguageId);
            // Update currentUser isNDASigned status
            dispatch(updateNDASignedStatus(true));
            // Refresh collaborator NDA status
            if (currentUser?.id) {
                await dispatch(fetchCollaboratorNDAStatusAsync(currentUser.id));
            }
            toast.success('NDA signed successfully');
            // Clean up
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
                setPdfBlobUrl('');
            }
        }
        catch (error) {
            console.error('Error signing NDA:', error);
            toast.error(error?.response?.data?.message || 'Failed to sign NDA');
        }
        finally {
            setIsSigning(false);
        }
    };
    return (_jsxs(Dialog, { open: open, maxWidth: "md", fullWidth: true, disableEscapeKeyDown: true, onClose: (event, reason) => {
            // Prevent closing by clicking outside or pressing ESC
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                return;
            }
        }, PaperProps: {
            sx: {
                borderRadius: 2,
                maxHeight: '90vh'
            }
        }, children: [_jsx(DialogTitle, { sx: { pb: 1 }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Icon, { icon: "eva:file-text-fill", width: 24, height: 24 }), _jsx(Typography, { variant: "h6", children: t('collaborators.ndaModalTitle') })] }) }), _jsxs(DialogContent, { dividers: true, sx: { px: isMobile ? 2 : 3, py: 2 }, children: [_jsx(Alert, { severity: "warning", variant: "outlined", icon: _jsx(Icon, { icon: "eva:alert-triangle-fill" }), sx: {
                            mb: 3,
                            backgroundColor: alpha(theme.palette.warning.main, 0.08)
                        }, children: _jsx(Typography, { variant: "body2", children: t('collaborators.ndaModalWarning') }) }), ndaDocuments.length > 1 && (_jsxs(FormControl, { fullWidth: true, sx: { mb: 3 }, children: [_jsx(InputLabel, { id: "nda-language-label", children: t('collaborators.selectLanguage') }), _jsx(Select, { labelId: "nda-language-label", id: "nda-language-select", value: selectedLanguageId, label: t('collaborators.selectLanguage'), onChange: (e) => setSelectedLanguageId(e.target.value), children: ndaDocuments.map((doc) => (_jsx(MenuItem, { value: doc.languageId || '', children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [doc.language?.flagUrl && (_jsx("img", { src: doc.language.flagUrl, alt: doc.language.name, style: { width: 24, height: 18, objectFit: 'cover', borderRadius: 2 } })), _jsx(Typography, { children: doc.language?.name || t('collaborators.ndaUnknownLanguage') })] }) }, doc.languageId))) })] })), pdfBlobUrl ? (_jsx(Box, { sx: {
                            width: '100%',
                            height: isMobile ? '400px' : '500px',
                            mb: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden'
                        }, children: _jsx("iframe", { src: pdfBlobUrl, style: {
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }, title: "NDA Document Preview" }) })) : (_jsxs(Box, { sx: { p: 3, textAlign: 'center', bgcolor: 'background.paper', mb: 3 }, children: [_jsx(CircularProgress, { size: 24 }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 2 }, children: t('collaborators.ndaLoadingDocument') })] })), _jsxs(Box, { children: [_jsxs(Typography, { variant: "body2", sx: { mb: 1, fontWeight: 500 }, children: [t('collaborators.ndaSignatureLabel'), " *"] }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mb: 1.5, display: 'block' }, children: t('collaborators.ndaSignatureHelper') }), _jsx(DigitalSignature, { ref: signatureRef, onSignatureChange: handleSignatureChange }), signatureError && (_jsx(Typography, { variant: "caption", color: "error", sx: { mt: 1, display: 'block' }, children: signatureError }))] })] }), _jsx(DialogActions, { sx: { px: 3, py: 2 }, children: _jsxs(Box, { sx: { width: '100%', display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", color: "primary", size: "large", disabled: !hasSignature || isSigning, onClick: handleClearSignature, sx: { flex: 1 }, children: t('collaborators.ndaClearButton') }), _jsx(Button, { variant: "contained", color: "primary", size: "large", onClick: handleSign, disabled: !hasSignature || isSigning, startIcon: isSigning ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : (_jsx(Icon, { icon: "eva:checkmark-fill" })), sx: { flex: 1 }, children: isSigning ? t('collaborators.ndaSigningButton') : t('collaborators.ndaSubmitButton') })] }) })] }));
};
export default NDAModal;
